import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DesignTestInputDto,
  DesignTestOutputDto
} from '../../../models/job-request-iterations-dto.interface';
import { calculateNafChemistryTitration } from '../../../shared/utils/naf-chemistry-calculations';

interface NafChemistryReportValues {
  alkalinity?: number;
  chlorides?: number;
  limeContent?: number;
  calcium?: number;
}

@Component({
  selector: 'app-naf-chemistry-test-report',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './naf-chemistry-test-report.component.html',
  styleUrl: './naf-chemistry-test-report.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NafChemistryTestReportComponent {
  @Input({ required: true }) test!: DesignTestInputDto;
  @Input({ required: true }) output!: DesignTestOutputDto;

  protected nafChemistryReportValues(): NafChemistryReportValues | null {
    const derivedValues = this.derivedValues();
    const alkalinity = this.numberValue([
      'salinity_nafMudAlkalinity',
      'nafMudAlkalinity',
      'naf mud alkalinity',
      'alkalinity'
    ], this.output) ?? this.numberValue([
      'salinity_nafMudAlkalinity',
      'nafMudAlkalinity',
      'naf mud alkalinity',
      'alkalinity'
    ], this.test) ?? derivedValues.alkalinity;
    const chlorides = this.numberValue([
      'salinity_wholeMudChlorides',
      'wholeMudChlorides',
      'wholemudchlorides',
      'chlorides'
    ], this.output) ?? this.numberValue([
      'salinity_wholeMudChlorides',
      'wholeMudChlorides',
      'wholemudchlorides',
      'chlorides'
    ], this.test) ?? derivedValues.chlorides;
    const limeContent = this.numberValue([
      'salinity_limeContent',
      'limeContent',
      'limecontent',
      'excesslime'
    ], this.output) ?? this.numberValue([
      'salinity_limeContent',
      'limeContent',
      'limecontent',
      'excesslime'
    ], this.test) ?? derivedValues.limeContent;
    const calcium = this.numberValue([
      'salinity_wholeMudCalcium',
      'wholeMudCalcium',
      'wholemudcalcium',
      'calcium'
    ], this.output) ?? this.numberValue([
      'salinity_wholeMudCalcium',
      'wholeMudCalcium',
      'wholemudcalcium',
      'calcium'
    ], this.test) ?? derivedValues.calcium;
    if ([alkalinity, chlorides, limeContent, calcium].every(value => value === undefined)) return null;
    return { alkalinity, chlorides, limeContent, calcium };
  }

  protected formatDecimal(value: number | undefined): string {
    return value === undefined ? '—' : value.toFixed(2);
  }

  protected formatWholeNumber(value: number | undefined): string {
    return value === undefined ? '—' : new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0
    }).format(value);
  }

  private derivedValues(): NafChemistryReportValues {
    const mudUsedVolume = this.firstNumber([
      'mudUsedVolume',
      'mudSampleVolume',
      'wholeMudSampleVolume'
    ], this.test, this.output) ?? this.firstNumber([
      'sampleWeight',
      'mudSampleWeight',
      'wholeMudSampleWeight'
    ], this.output, this.test) ?? 1;

    if (mudUsedVolume <= 0) return {};

    const alkalineDemand = this.firstNumber([
      'alkalineDemand',
      'alkalineDemandVolume'
    ], this.output, this.test);
    const acidVolume = this.firstNumber([
      'acidVolume',
      'wholeMudAcidVolume'
    ], this.output, this.test);
    const silverNitrateVolume = this.firstNumber([
      'agNo3Volume',
      'silverNitrateVolume',
      'wholeMudSilverNitrateVolume'
    ], this.output, this.test);
    const edtaVolume = this.firstNumber([
      'edtaVolume',
      'EDTAVolume',
      'wholeMudEdtaVolume'
    ], this.output, this.test);
    const silverNitrateConcentration = this.firstNumber([
      'agNo3Concentration',
      'silverNitrateConcentration',
      'wholeMudSilverNitrateConcentration'
    ], this.output, this.test);
    const edtaConcentration = this.firstNumber([
      'edtaConcentration',
      'wholeMudEdtaConcentration'
    ], this.output, this.test);
    const titration = calculateNafChemistryTitration({
      mudUsedVolume,
      alkalineDemand: alkalineDemand ?? 0,
      acidVolume: acidVolume ?? 0,
      silverNitrateVolume: silverNitrateVolume ?? 0,
      edtaVolume: edtaVolume ?? 0,
      silverNitrateConcentration: silverNitrateConcentration ?? 10000,
      edtaConcentration: edtaConcentration ?? 4000
    });
    const hasAlkalinityInputs = alkalineDemand !== undefined && acidVolume !== undefined;

    return {
      alkalinity: hasAlkalinityInputs ? titration?.nafMudAlkalinity : undefined,
      limeContent: hasAlkalinityInputs ? titration?.limeContent : undefined,
      chlorides: silverNitrateVolume !== undefined
        ? titration?.wholeMudChlorides ?? undefined
        : undefined,
      calcium: edtaVolume !== undefined
        ? titration?.wholeMudCalcium ?? undefined
        : undefined
    };
  }

  private firstNumber(
    names: string[],
    ...sources: Array<DesignTestInputDto | DesignTestOutputDto>
  ): number | undefined {
    for (const source of sources) {
      const value = this.numberValue(names, source);
      if (value !== undefined) return value;
    }

    return undefined;
  }

  private numberValue(names: string[], source: DesignTestInputDto | DesignTestOutputDto): number | undefined {
    return this.toNumber(this.value(names, source));
  }

  private value(names: string[], source: DesignTestInputDto | DesignTestOutputDto): string | null {
    const acceptedNames = new Set(names.map(name => this.normalize(name)));
    const fields = 'parsedValues' in source ? source.parsedValues : source.parsedInputValues;
    const parsedField = fields.find(field => acceptedNames.has(this.normalize(field.label)));
    return parsedField?.value?.trim() || this.xmlValue(source.content, acceptedNames);
  }

  private xmlValue(content: string | null, names: Set<string>): string | null {
    if (!content) return null;

    const document = new DOMParser().parseFromString(content, 'application/xml');
    if (document.querySelector('parsererror')) return null;

    for (const element of Array.from(document.getElementsByTagName('*'))) {
      if (element.localName.toLowerCase() === 'parameter') {
        if (!names.has(this.normalize(element.getAttribute('key')))) continue;
      } else if (!names.has(this.normalize(element.localName))) {
        continue;
      }

      if (element.children.length === 0) {
        const value = element.textContent?.trim();
        if (value) return value;
      }
    }

    return null;
  }

  private normalize(value: string | null | undefined): string {
    return (value ?? '').replace(/[\s_\-/,().:%]+/g, '').toLowerCase();
  }

  private toNumber(value: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : undefined;
  }
}